// Blog posts — único dono de `blog_posts`. Leitura pública (anon, só
// publicados) e leitura/escrita admin têm keys separadas porque a RLS
// devolve conjuntos diferentes (docs/specs/standards/hooks-and-data.md).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BlogCategoryRef {
  id: string;
  name: string;
  slug: string;
}

/** Linha do blog público (lista e post) — só campos que a página usa. */
export interface PublicBlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
  cover_image_url: string | null;
  published_at: string | null;
  created_at: string;
  category_id: string | null;
  blog_categories: BlogCategoryRef | null;
}

/** Linha da lista do admin (inclui rascunhos). */
export interface AdminBlogPost {
  id: string;
  title: string;
  slug: string;
  published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  cover_image_url: string | null;
  blog_categories: { name: string } | null;
}

/** O que o editor lê e grava. */
export interface BlogPostDraft {
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  cover_image_url: string | null;
  category_id: string | null;
  published: boolean;
  published_at: string | null;
}

const PUBLIC_COLUMNS =
  "id, title, slug, excerpt, content, cover_image_url, published_at, created_at, category_id, blog_categories(id, name, slug)";

// ─── público ────────────────────────────────────────────────────────────────

export function usePublicBlogPosts() {
  return useQuery({
    queryKey: ["public-blog-posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select(PUBLIC_COLUMNS)
        .eq("published", true)
        .order("published_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as PublicBlogPost[];
    },
  });
}

/** `null` quando não existe ou não está publicado. */
export function usePublicBlogPostBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ["public-blog-post", slug],
    enabled: !!slug,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*, blog_categories(name)")
        .eq("slug", slug!)
        .eq("published", true)
        .maybeSingle();
      if (error) throw error;
      return (
        (data as unknown as
          (PublicBlogPost & { blog_categories: { name: string } | null }) | null) ?? null
      );
    },
  });
}

/** Posts publicados para o seletor "post de blog" da bio (antes em useBioLinks). */
export function usePublishedBlogPosts() {
  return useQuery({
    queryKey: ["public-blog-posts", "picker"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, title, slug, cover_image_url")
        .eq("published", true)
        .order("published_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

// ─── admin ──────────────────────────────────────────────────────────────────

export function useBlogPosts() {
  return useQuery({
    queryKey: ["blog-posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select(
          "id, title, slug, published, published_at, created_at, updated_at, cover_image_url, blog_categories(name)",
        )
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as AdminBlogPost[];
    },
  });
}

export function useBlogPost(id: string | undefined) {
  return useQuery({
    queryKey: ["blog-post", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("title, slug, excerpt, content, cover_image_url, category_id, published")
        .eq("id", id!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

function useInvalidateBlog() {
  const qc = useQueryClient();
  return (id?: string) => {
    qc.invalidateQueries({ queryKey: ["blog-posts"] });
    qc.invalidateQueries({ queryKey: ["public-blog-posts"] });
    qc.invalidateQueries({ queryKey: ["content-hub-counts"] });
    qc.invalidateQueries({ queryKey: ["blog-categories"] });
    if (id) qc.invalidateQueries({ queryKey: ["blog-post", id] });
  };
}

export function useCreateBlogPost() {
  const invalidate = useInvalidateBlog();
  return useMutation({
    mutationFn: async (draft: BlogPostDraft) => {
      const { error } = await supabase.from("blog_posts").insert(draft);
      if (error) throw error;
    },
    onSuccess: () => invalidate(),
  });
}

export function useUpdateBlogPost() {
  const invalidate = useInvalidateBlog();
  return useMutation({
    mutationFn: async ({ id, draft }: { id: string; draft: Partial<BlogPostDraft> }) => {
      const { error } = await supabase.from("blog_posts").update(draft).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_d, v) => invalidate(v.id),
  });
}

/** Publica (`published_at = now`) ou despublica (`published_at = null`). */
export function useToggleBlogPostPublished() {
  const invalidate = useInvalidateBlog();
  return useMutation({
    mutationFn: async ({ id, published }: { id: string; published: boolean }) => {
      const { error } = await supabase
        .from("blog_posts")
        .update({ published, published_at: published ? new Date().toISOString() : null })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_d, v) => invalidate(v.id),
  });
}

export function useDeleteBlogPost() {
  const invalidate = useInvalidateBlog();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("blog_posts").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_d, id) => invalidate(id),
  });
}
